// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import PageBreadcrumbs from "@insite/content-library/Components/PageBreadcrumbs";
import Button from "@insite/mobius/Button";
import Modal from "@insite/mobius/Modal";
import Page from "@insite/mobius/Page";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import loadTermsInfo from "@mcmc/App/Handlers/Account/LoadTermsInfo";
import setAcceptUserTerms from "@mcmc/App/Handlers/Account/SetAcceptUserTerms";
import setIsMoblico from "@mcmc/App/Handlers/Layout/setIsMoblico";
import McMcApplicationState from "@mcmc/App/Store/McMcApplicationState";
import { getIsMoblico } from "@mcmc/App/Store/Selectors/LayoutSelectors";
import Modals from "@mcmc/Overrides/Components/Modals";
import EllipsisSpinner from "@mcmc/XDL/Components/Common/EllipsisSpinner";
import { layoutContainer } from "@mcmc/XDL/Utilities";
import { getIsMoblicoCookie, setIsMoblicoCookieIfNeeded } from "@mcmc/XDL/Utilities/moblico";
import * as React from "react";
import { ReactNode } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface PageLayoutProps {
    showHeader: boolean;
    header: ReactNode;
    showCompactHeader: boolean;
    compactHeader: ReactNode;
    showBreadcrumbs: boolean;
    pageContent: ReactNode;
    showFooter: boolean;
    footer: ReactNode;
}

const mapStateToProps = (state: McMcApplicationState) => {
    let isMoblicoCookie = getIsMoblicoCookie();
    const location = getLocation(state);
    if (!isMoblicoCookie) {
        const isMoblicoParameter = parseQueryString<{ isMoblico: string }>(location.search).isMoblico;
        isMoblicoCookie = setIsMoblicoCookieIfNeeded(isMoblicoParameter);
    }
    const userId = parseQueryString<{ userId?: string }>(location.search).userId;
    const session = getSession(state);

    return {
        isFullWidth: state.xLayout.isFullWidth,
        breadcrumbActions: state.xLayout.breadcrumbActions,
        isMoblicoCookie,
        isMoblico: getIsMoblico(state),
        userId: session.properties.userId || userId,
        acceptTermsInfo: state.xLayout.acceptTermsInfo,
        isAuthenticated: session && (session.isAuthenticated || session.rememberMe) && !session.isGuest,
        byPassAcceptTerms: getCurrentPage(state).fields.byPassAcceptTerms,
    };
};

const mapDispatchToProps = {
    setIsMoblico,
    loadTermsInfo,
    setAcceptUserTerms,
    signOut,
};

type Props = PageLayoutProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

// This doesn't currently support HMR
class PageLayout extends React.Component<Props> {
    state = { isAcceptingTerms: false };
    static contextType = ToasterContext;
    context!: React.ContextType<typeof ToasterContext>;

    componentDidMount(): void {
        if (this.props.isMoblicoCookie && !this.props.isMoblico) {
            this.props.setIsMoblico({ isMoblico: this.props.isMoblicoCookie });
        }
        if (this.props.userId && !this.props.acceptTermsInfo) {
            this.props.loadTermsInfo({
                userId: this.props.userId,
            });
        }
    }

    render() {
        const {
            showHeader,
            header,
            showBreadcrumbs,
            pageContent,
            showFooter,
            footer,
            showCompactHeader,
            compactHeader,
            isFullWidth,
            breadcrumbActions,
            isMoblico,
            setIsMoblico,
            isAuthenticated,
            byPassAcceptTerms,
        } = this.props;

        const isTermsModalOpen =
            this.props.acceptTermsInfo?.acceptTerms?.toLowerCase() === "false" && isAuthenticated && !byPassAcceptTerms;
        const { isAcceptingTerms } = this.state;

        return (
            <>
                {showHeader && header}
                {showCompactHeader && compactHeader}
                {showBreadcrumbs && (
                    <Page
                        as="div"
                        css={[
                            layoutContainer,
                            css`
                                display: flex;
                                flex-wrap: wrap;
                                align-items: center;
                                justify-content: flex-end;
                                padding-top: 35px;
                                padding-bottom: 25px;
                                ${({ theme }) =>
                                    breakpointMediaQueries(
                                        theme,
                                        [
                                            css`
                                                overflow: initial;
                                            `,
                                            css`
                                                overflow: initial;
                                                margin: -5px;
                                                > * {
                                                    padding: 5px;
                                                }
                                            `,
                                        ],
                                        "max",
                                    )}

                                @media print {
                                    padding: 10px;
                                }
                            `,
                        ]}
                    >
                        <PageBreadcrumbs />
                        {breadcrumbActions}
                    </Page>
                )}
                <StyledWrapper
                    css={[
                        layoutContainer,
                        css`
                            break-before: avoid;
                        `,
                    ]}
                    className={isFullWidth ? "full-width" : ""}
                >
                    {pageContent}
                </StyledWrapper>
                {showFooter && footer}
                <Modals isGlobalPage={true} />
                <Modal headline={translate("Accept terms to continue")} isOpen={isTermsModalOpen} isCloseable={false}>
                    <Typography
                        css={css`
                            padding-top: 10px;
                        `}
                    >
                        {siteMessage("McMc_CreateAccount_AcceptTerms")}
                    </Typography>
                    <StyledWrapper className="modal-actions">
                        <Button
                            onClick={() => {
                                this.setState({ isAcceptingTerms: true });
                                this.props.setAcceptUserTerms({
                                    username: this.props.acceptTermsInfo!.username,
                                    token: this.props.acceptTermsInfo!.token,
                                    onSuccess: () => {
                                        this.props.loadTermsInfo({ userId: this.props.userId! });
                                    },
                                    onError: () => {
                                        this.context.addToast({
                                            body: siteMessage("McMc_AcceptTerms_Failed"),
                                            messageType: "danger",
                                        });
                                    },
                                    onComplete: () => {
                                        this.setState({ isAcceptingTerms: false });
                                    },
                                });
                            }}
                            data-test-selector="acceptTermsModal_Accept"
                            disabled={isAcceptingTerms}
                        >
                            <EllipsisSpinner label={translate("Accept Terms")} showSpinner={isAcceptingTerms} />
                        </Button>
                        <Button
                            onClick={this.props.signOut}
                            variant="secondary"
                            data-test-selector="acceptTermsModal_signOut"
                        >
                            {translate("Sign Out")}
                        </Button>
                    </StyledWrapper>
                </Modal>
            </>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(PageLayout);
